import React, { useEffect, useState } from 'react'
import { FormControl } from '@mui/material'
import Select, { MultiValue } from 'react-select'
import { SelectChangeEvent } from '@mui/material/Select'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { fetchActionsWithoutProject } from '../../redux/actions/actionActions'
import { useAppDispatch, AppDispatch } from '../../redux/store'
import { selectActionsWithoutProject } from '../../redux/actions/actionSelectors'
import { useExclusiveMenu } from '../../hooks/useExclusiveMenu'
import { incyiaSelectStyles } from '../../utils/incyiaSelectStyles'
import { createOptionProps } from '../../components/common/CreateOptionMenu'

interface SelectActionProps {
  value: string[]
  onChange: (event: SelectChangeEvent<string[]>) => void
  MissionId: string
  onCreateNew?: (search: string) => void
}

const SelectAction: React.FC<SelectActionProps> = ({
  value,
  onChange,
  MissionId,
  onCreateNew,
}) => {
  const menu = useExclusiveMenu()
  const { t } = useTranslation()
  const dispatch: AppDispatch = useAppDispatch()
  const actionsList = useSelector(selectActionsWithoutProject)
  const [optionsLoading, setOptionsLoading] = useState(false)

  useEffect(() => {
    if (!MissionId) {
      setOptionsLoading(false)
      return
    }
    let active = true
    setOptionsLoading(true)
    dispatch(fetchActionsWithoutProject(MissionId)).finally(() => {
      if (active) setOptionsLoading(false)
    })
    return () => {
      active = false
    }
  }, [dispatch, MissionId])

  const options = (actionsList ?? []).map((action) => ({
    value: action._id || '',
    label: action.ActionName || '',
  }))

  const selectedOptions = options.filter((opt) => value?.includes(opt.value))

  const handleSelectChange = (
    selected: MultiValue<{ value: string; label: string }>
  ) => {
    onChange({
      target: {
        name: 'ActionId',
        value: selected ? selected.map((item) => item.value) : [],
      },
    } as SelectChangeEvent<string[]>)
  }

  const canCreate = Boolean(onCreateNew) && !optionsLoading && options.length === 0

  return (
    <div style={{ width: '100%', position: 'relative' }}>
      <FormControl fullWidth style={{ width: '100%' }}>
        <Select
          isMulti
          value={selectedOptions}
          onChange={handleSelectChange}
          options={options}
          isLoading={optionsLoading}
          placeholder={
            optionsLoading
              ? t('selectOptions.loading')
              : t('priorityTasksByMissionPage.tasks')
          }
          isDisabled={optionsLoading || !MissionId}
          noOptionsMessage={() => t('tables.noData')}
          {...menu}
          styles={incyiaSelectStyles}
          {...createOptionProps(
            canCreate && onCreateNew
              ? { label: t('quickCreate.new.task'), onCreate: onCreateNew }
              : undefined
          )}
        />
      </FormControl>
    </div>
  )
}

export default SelectAction
